import type { ConnectionRequest } from 'types/connections';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import ShieldCheckTrusted from 'svg/ShieldCheckTrusted';
import BadgeRating from 'components/BadgeRating';
import Button from 'components/Button';
import classNames from 'styles/utils/classNames';

interface Props {
  requests: ConnectionRequest[];
  /** Figma uses "Connection requests" on the You card and "Requests" on the page. */
  title?: string;
  onSeeRequest: (request: ConnectionRequest) => void;
  className?: string;
}

export default function ConnectionRequestsBanner({
  requests,
  title = 'Connection requests',
  onSeeRequest,
  className,
}: Props) {
  if (!requests.length) {
    return null;
  }

  return (
    <div
      className={classNames(
        'flex flex-col gap-ds-md rounded-[1.25rem] bg-brand-fire-600 bg-[radial-gradient(60%_160%_at_80%_18%,theme(colors.brand-fire-500),transparent_70%)] p-ds-md shadow-[0px_4px_10px_0px_rgba(255,92,51,0.5)]',
        className,
      )}
    >
      <p className="typography-product-card-title-mobile text-color-text-darkmode-primary">
        {title}: {requests.length}
      </p>
      <ul className="flex flex-col gap-ds-md divide-y divide-color-text-lightmode-invert/30">
        {requests.map((request) => (
          <li key={request.id} className="flex items-center gap-ds-lg pt-ds-md first:pt-0">
            <div className="flex min-w-0 flex-1 items-center gap-ds-sm">
              <div className="relative size-10 shrink-0">
                <img
                  src={getProfileImageUrlOrPlaceholder({ path: request.player.profileImagePath })}
                  alt=""
                  className="size-full rounded-full border-2 border-color-bg-lightmode-primary object-cover"
                />
                {request.player.isVerified && (
                  <span className="absolute -bottom-1 -right-1 rounded-full bg-color-bg-lightmode-primary p-[0.125rem]">
                    <ShieldCheckTrusted className="block size-3" />
                  </span>
                )}
              </div>
              <div className="flex min-w-0 flex-col gap-ds-xs">
                <p className="typography-product-subheading-mobile truncate text-color-text-darkmode-primary">
                  {request.player.fullName}
                </p>
                {!!request.player.skillLevel && (
                  <BadgeRating
                    level={request.player.skillLevel}
                    sport={request.player.sport}
                    variant="neutral"
                    size="xs"
                    className="h-4 gap-0.5 self-start px-ds-xs"
                  />
                )}
              </div>
            </div>
            <Button
              variant="primary-light"
              size="sm"
              isInline
              onClick={() => onSeeRequest(request)}
              className="shrink-0"
            >
              See request
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
